<template>
  <el-dialog :title="$t('AccountManagement.work.reviewTitle')" :visible.sync="visible" custom-class="timesheet-review-dialog" width="94%" top="5vh" append-to-body @closed="cancelLoad">
    <div v-loading="loading" class="timesheet-review">
      <div class="review-toolbar">
        <strong>{{ workDate ? workDate.substring(0, 10) : '' }}</strong>
        <el-select v-model="department" :placeholder="$t('AccountManagement.work.reviewAll')" clearable filterable>
          <el-option :label="$t('AccountManagement.work.reviewAll')" value="" />
          <el-option v-for="item in departments" :key="String(item.value)" :label="item.label" :value="String(item.value)" />
        </el-select>
        <el-radio-group v-model="sourceType" @change="load">
          <el-radio-button label="actual">{{ $t('AccountManagement.work.actual') }}</el-radio-button>
          <el-radio-button label="overtime">{{ $t('AccountManagement.work.reviewOvertime') }}</el-radio-button>
        </el-radio-group>
        <el-button icon="el-icon-refresh" :disabled="loading" @click="refresh">{{ $t('AccountManagement.work.reviewRefresh') }}</el-button>
      </div>
      <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
      <el-alert v-if="invalidCount" :title="$t('AccountManagement.work.reviewInvalid', { count: invalidCount })" type="warning" :closable="false" show-icon />
      <el-alert v-if="conflictCount" :title="$t('AccountManagement.work.reviewConflict', { count: conflictCount })" type="warning" :closable="false" show-icon />
      <el-alert v-if="gapCount" :title="$t('AccountManagement.work.reviewGapCount', { count: gapCount })" type="warning" :closable="false" show-icon />
      <div class="review-legend">
        <el-button v-if="sourceType === 'overtime' && rows.length" type="text" icon="el-icon-position" @click="scrollToRecords">{{ $t('AccountManagement.work.reviewLocate') }}</el-button>
        <span v-for="item in legend" :key="item"><i :style="{ background: color(item) }"></i>{{ label(item) }}</span>
        <span>{{ $t('AccountManagement.work.reviewPeople', { count: rows.length }) }}</span>
        <span class="review-overlap-key"><i></i>{{ $t('AccountManagement.work.reviewOverlap') }}</span>
        <span class="review-gap-key"><i></i>{{ $t('AccountManagement.work.reviewGap') }}</span>
      </div>
      <div v-if="rows.length" ref="chart" class="review-scroll" role="region" :aria-label="$t('AccountManagement.work.reviewTitle')" tabindex="0">
        <div class="review-chart" :style="{ minWidth: (180 + ticks.length * 64) + 'px', '--review-tick-count': ticks.length }">
          <div class="review-header">
            <div class="review-name">{{ $t('AccountManagement.staff.name') }}</div>
            <div v-for="tick in ticks" :key="tick" class="review-tick">{{ formatMinutes(tick) }}</div>
          </div>
          <div v-for="person in rows" :key="person.key" class="review-person" :style="{ minHeight: (person.lanes * 42 + 16) + 'px' }">
            <div class="review-name">
              <strong>{{ person.name }}</strong>
              <span v-if="person.conflicts.length" class="review-warning"><i class="el-icon-warning"></i> {{ $t('AccountManagement.work.reviewOverlap') }}</span>
              <span v-if="person.gaps.length" class="review-warning review-gap-warning"><i class="el-icon-warning-outline"></i> {{ $t('AccountManagement.work.reviewGap') }}</span>
            </div>
            <div class="review-track">
              <el-tooltip v-for="(gap, index) in person.gaps" :key="'gap:' + index" placement="top" :content="person.name + ' · ' + $t('AccountManagement.work.reviewGap') + ' · ' + formatMinutes(gap.start) + ' – ' + formatMinutes(gap.end)">
                <div class="review-gap" :style="gapStyle(gap)" tabindex="0">{{ $t('AccountManagement.work.reviewGap') }}</div>
              </el-tooltip>
              <el-tooltip v-for="record in person.records" :key="record.key" placement="top" :open-delay="150">
                <div slot="content">
                  <div>{{ person.name }} · {{ formatMinutes(record.start) }} – {{ formatMinutes(record.end) }}</div>
                  <div>{{ recordLabel(record) }}</div>
                  <div v-if="record.overlap">{{ $t('AccountManagement.work.reviewOverlap') }}</div>
                </div>
                <div class="review-bar" :class="{ 'has-overlap': record.overlap }" :style="barStyle(record)" tabindex="0">
                  <span :style="{ background: color(record.department) }">{{ recordLabel(record) }}</span>
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="!loading && !error" class="review-empty">{{ $t('AccountManagement.work.reviewEmpty') }}</div>
      <div v-if="conflictCount || gapCount" class="review-conflicts">
        <template v-for="person in rows">
          <div v-for="(conflict, index) in person.conflicts" :key="person.key + ':' + index">
            <i class="el-icon-warning"></i> {{ $t('AccountManagement.work.reviewOverlap') }} · {{ person.name }} · {{ formatMinutes(conflict.start) }} – {{ formatMinutes(conflict.end) }}<span v-if="conflictLabel(conflict)"> · {{ conflictLabel(conflict) }}</span>
          </div>
          <div v-for="(gap, index) in person.gaps" :key="person.key + ':gap:' + index" class="review-gap-warning">
            <i class="el-icon-warning-outline"></i> {{ $t('AccountManagement.work.reviewGap') }} · {{ person.name }} · {{ formatMinutes(gap.start) }} – {{ formatMinutes(gap.end) }}
          </div>
        </template>
      </div>
    </div>
    <div slot="footer"><el-button @click="visible = false">{{ $t('AccountManagement.work.close') }}</el-button></div>
  </el-dialog>
</template>

<script>
import { funConfigApi } from '@/api';
import { buildReviewRows, filterReviewRows, formatMinutes, reviewTicks } from './timesheetReview';
import { normalizeReviewResponse } from './reviewResponse';

const COLORS = ['#2458a6', '#137c70', '#9654a0', '#b45c12', '#5266a3', '#a64063', '#587628', '#147f9f'];
export default {
  name: 'ReviewDialog',
  props: {
    departments: { type: Array, default: () => [] },
    departmentLabels: { type: Array, default: () => [] },
  },
  data() {
    return { visible: false, loading: false, workDate: '', department: '', sourceType: 'actual', allRows: [], invalidCount: 0, error: '', requestId: 0 };
  },
  computed: {
    rows() { return filterReviewRows(this.allRows, this.department); },
    conflictCount() { return this.rows.reduce((sum, person) => sum + person.conflicts.length, 0); },
    gapCount() { return this.rows.reduce((sum, person) => sum + person.gaps.length, 0); },
    legend() { return Array.from(new Set(this.rows.reduce((all, person) => all.concat(...person.records.map(record => record.departmentIds)), []))).sort(); },
    colorKeys() { return Array.from(new Set(this.departmentLabels.map(item => String(item.value)).concat(this.legend))).sort(); },
    ticks() {
      return reviewTicks(this.rows, this.sourceType);
    },
  },
  watch: {
    department() { this.$nextTick(this.scrollToRecords); },
  },
  beforeDestroy() { this.cancelLoad(); },
  methods: {
    formatMinutes,
    gapStyle(gap) {
      const duration = this.ticks.length * 30;
      return { left: ((gap.start - this.ticks[0]) / duration * 100) + '%', width: ((gap.end - gap.start) / duration * 100) + '%' };
    },
    scrollToRecords() {
      const chart = this.$refs.chart;
      if (!chart) return;
      const firstStart = this.rows.reduce((first, person) => person.records.reduce((min, record) => Math.min(min, record.start), first), 1440);
      // Leave one half-hour of context before the earliest shift. The name column
      // remains sticky while the full-day timeline scrolls horizontally.
      chart.scrollLeft = this.sourceType === 'overtime' && this.rows.length
        ? Math.max(0, (Math.floor((firstStart - this.ticks[0]) / 30) - 1) * (chart.scrollWidth - 180) / this.ticks.length)
        : 0;
    },
    label(value) {
      const item = this.departmentLabels.find(item => String(item.value) === String(value) || item.label === value);
      return item ? item.label : value;
    },
    color(value) { return COLORS[Math.max(0, this.colorKeys.indexOf(value)) % COLORS.length]; },
    recordLabel(record) {
      return record.departmentIds.map(this.label).map(label => label == null ? '' : String(label).trim()).filter(Boolean).join(' / ');
    },
    conflictLabel(conflict) { return conflict.records.map(this.recordLabel).filter(Boolean).join(' / '); },
    barStyle(record) {
      return { ...this.gapStyle(record), top: (record.lane * 42 + 8) + 'px', height: '30px' };
    },
    show(date) {
      this.workDate = date;
      this.department = '';
      this.sourceType = 'actual';
      this.visible = true;
      this.load();
    },
    cancelLoad() { this.requestId++; this.loading = false; },
    reloadIfVisible() {
      this.cancelLoad();
      if (this.visible) this.load();
    },
    refresh() {
      this.load();
    },
    async load() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = '';
      this.allRows = [];
      this.invalidCount = 0;
      const workDate = this.workDate;
      const api = this.sourceType === 'actual' ? funConfigApi.getWorkforceReviewActual : funConfigApi.getWorkforceReviewOvertime;
      try {
        const response = await api({ workDate });
        if (requestId !== this.requestId) return;
        const sources = normalizeReviewResponse(response);
        const result = buildReviewRows(sources, workDate, this.departmentLabels);
        result.rows.forEach(person => person.records.forEach(record => {
          const item = this.departmentLabels.find(item => String(item.value) === record.department || item.label === record.department);
          if (item) record.department = String(item.value);
          record.departmentIds = record.departmentIds.map(department => {
            const option = this.departmentLabels.find(item => String(item.value) === department || item.label === department);
            return option ? String(option.value) : department;
          });
        }));
        this.allRows = result.rows;
        this.invalidCount = result.invalidCount;
        this.$nextTick(this.scrollToRecords);
      } catch (error) {
        if (requestId === this.requestId) this.error = this.$t('AccountManagement.work.reviewRequestError');
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
  },
};
</script>
